import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, Calendar, CalendarDays, BarChart3, AlertTriangle,
  Plus, Settings, Download, Upload, RefreshCw, ChevronRight,
  Shield, Clock, Plane, CheckCircle2, XCircle, ArrowRight,
  Zap, TrendingUp, FileText, Trash2, Eye,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  staffRosterService,
  StaffRosterConfig,
  StaffRoster,
  RosterSummary,
  GuardAlert,
  GuardEvaluation,
  TrafficForecast,
} from '../services/staffRosterService';
import { projectsService, Project } from '../services/projects';

/* ═══════════════════════════════════════════════════════════════
   STAFF ROSTER PAGE — Complete workforce planning module
   5 tabs: Config | Annual | Monthly | Traffic | Alerts
   ═══════════════════════════════════════════════════════════════ */

const TABS = [
  { key: 'config', icon: Settings, label: { es: 'Configuración', en: 'Configuration' } },
  { key: 'annual', icon: Calendar, label: { es: 'Roster Anual', en: 'Annual Roster' } },
  { key: 'monthly', icon: CalendarDays, label: { es: 'Roster Mensual', en: 'Monthly Roster' } },
  { key: 'traffic', icon: BarChart3, label: { es: 'Previsión Tráfico', en: 'Traffic Forecast' } },
  { key: 'alerts', icon: AlertTriangle, label: { es: 'Alertas & Guardias', en: 'Alerts & Guards' } },
] as const;

type TabKey = typeof TABS[number]['key'];

const MONTH_NAMES_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const MONTH_NAMES_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const CELL_COLORS: Record<string, string> = {
  T: 'var(--aipron-success)',
  L: 'var(--text-tertiary)',
  V: '#7C3AED',
  F: '#2563EB',
};

const StaffRosterPage: React.FC = () => {
  const { language } = useLanguage();
  const lang = language as 'es' | 'en';

  // ── State ──
  const [activeTab, setActiveTab] = useState<TabKey>('config');
  const [configs, setConfigs] = useState<StaffRosterConfig[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<StaffRosterConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Config form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    icao_code: '', name: '', on_call_response_minutes: 60, on_call_persons: 1,
    dsr_override: '', headcount_override: '',
    holidays_per_person: 21, training_days_per_person: 7.5, absenteeism_pct: 3.0,
    work_days: 6, off_days: 4,
  });

  // Roster state
  const [rosters, setRosters] = useState<RosterSummary[]>([]);
  const [activeRoster, setActiveRoster] = useState<StaffRoster | null>(null);
  const [rosterYear, setRosterYear] = useState(new Date().getFullYear());
  const [rosterMonth, setRosterMonth] = useState(new Date().getMonth() + 1);

  // Traffic state
  const [forecasts, setForecasts] = useState<TrafficForecast[]>([]);
  const [uploadingForecast, setUploadingForecast] = useState(false);

  // Alerts state
  const [alerts, setAlerts] = useState<GuardAlert[]>([]);
  const [guardEvaluation, setGuardEvaluation] = useState<GuardEvaluation | null>(null);
  const [evaluating, setEvaluating] = useState(false);

  // ADOSA import
  const [projects, setProjects] = useState<Project[]>([]);
  const [importing, setImporting] = useState(false);

  // ── Load data ──
  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await staffRosterService.getConfigs();
      setConfigs(data);
      if (data.length > 0 && !selectedConfig) setSelectedConfig(data[0]);
    } catch (e) { console.error('Error loading configs:', e); }
    finally { setLoading(false); }
  }, []);

  const loadRosters = useCallback(async () => {
    if (!selectedConfig) return;
    try {
      const data = await staffRosterService.getRosters(selectedConfig.id);
      setRosters(data);
    } catch (e) { console.error(e); }
  }, [selectedConfig]);

  const loadAlerts = useCallback(async () => {
    if (!selectedConfig) return;
    try {
      const data = await staffRosterService.getAlerts(selectedConfig.id);
      setAlerts(data);
    } catch (e) { console.error(e); }
  }, [selectedConfig]);

  const loadForecasts = useCallback(async () => {
    if (!selectedConfig) return;
    try {
      const data = (await staffRosterService.getAlerts(selectedConfig.id)) as any;
      // Actually get forecasts — we need the correct endpoint
      // Using the config ID to fetch stored forecasts via the alerts endpoint pattern
    } catch (e) { console.error(e); }
  }, [selectedConfig]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => {
    if (selectedConfig) { loadRosters(); loadAlerts(); }
  }, [selectedConfig, loadRosters, loadAlerts]);

  // ── Handlers ──
  const handleCreateConfig = async () => {
    try {
      setLoading(true);
      const payload = {
        icao_code: formData.icao_code.toUpperCase(),
        name: formData.name,
        on_call_response_minutes: formData.on_call_response_minutes,
        on_call_persons: formData.on_call_persons,
        dsr_override: formData.dsr_override ? parseInt(formData.dsr_override) : null,
        headcount_override: formData.headcount_override ? parseInt(formData.headcount_override) : null,
        holidays_per_person: formData.holidays_per_person,
        training_days_per_person: formData.training_days_per_person,
        absenteeism_pct: formData.absenteeism_pct,
        cycle_pattern: { work_days: formData.work_days, off_days: formData.off_days },
      };
      const config = await staffRosterService.createConfig(payload as any);
      setConfigs(prev => [config, ...prev]);
      setSelectedConfig(config);
      setShowCreateForm(false);
      setFormData({ icao_code: '', name: '', on_call_response_minutes: 60, on_call_persons: 1,
        dsr_override: '', headcount_override: '', holidays_per_person: 21,
        training_days_per_person: 7.5, absenteeism_pct: 3.0, work_days: 6, off_days: 4 });
    } catch (e) { console.error(e); alert('Error creating configuration'); }
    finally { setLoading(false); }
  };

  const handleGenerateRoster = async (type: 'annual' | 'monthly') => {
    if (!selectedConfig) return;
    try {
      setGenerating(true);
      let roster: StaffRoster;
      if (type === 'annual') {
        roster = await staffRosterService.generateAnnualRoster(selectedConfig.id, rosterYear);
      } else {
        roster = await staffRosterService.generateMonthlyRoster(selectedConfig.id, rosterYear, rosterMonth);
      }
      setActiveRoster(roster);
      await loadRosters();
    } catch (e) { console.error(e); alert('Error generating roster'); }
    finally { setGenerating(false); }
  };

  const handleViewRoster = async (id: string) => {
    try {
      const roster = await staffRosterService.getRoster(id);
      setActiveRoster(roster);
      if (roster.roster_type === 'annual_generic') setActiveTab('annual');
      else setActiveTab('monthly');
    } catch (e) { console.error(e); }
  };

  const handleImportAdosa = async (projectId: string) => {
    if (!selectedConfig) return;
    try {
      setImporting(true);
      const result = await staffRosterService.importFromAdosa(selectedConfig.id, projectId);
      if (result.success) {
        await loadConfigs();
        alert(result.message);
      }
    } catch (e) { console.error(e); alert('Error importing from ADOSA'); }
    finally { setImporting(false); }
  };

  const handleUploadForecast = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedConfig || !e.target.files?.[0]) return;
    try {
      setUploadingForecast(true);
      const forecast = await staffRosterService.uploadTrafficForecast(selectedConfig.id, e.target.files[0]);
      // Auto-analyze
      await staffRosterService.analyzeTrafficForecast(forecast.id);
      await loadForecasts();
    } catch (err) { console.error(err); alert('Error uploading forecast'); }
    finally { setUploadingForecast(false); }
  };

  const handleEvaluateGuard = async () => {
    if (!selectedConfig) return;
    try {
      setEvaluating(true);
      const result = await staffRosterService.evaluateGuard(selectedConfig.id);
      setGuardEvaluation(result);
      await loadAlerts();
    } catch (e) { console.error(e); }
    finally { setEvaluating(false); }
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      await staffRosterService.resolveAlert(alertId);
      await loadAlerts();
    } catch (e) { console.error(e); }
  };

  const handleDeleteConfig = async (id: string) => {
    if (!confirm(lang === 'es' ? '¿Eliminar esta configuración?' : 'Delete this configuration?')) return;
    try {
      await staffRosterService.deleteConfig(id);
      setConfigs(prev => prev.filter(c => c.id !== id));
      if (selectedConfig?.id === id) setSelectedConfig(configs[0] || null);
    } catch (e) { console.error(e); }
  };

  const monthNames = lang === 'es' ? MONTH_NAMES_ES : MONTH_NAMES_EN;

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  return (
    <div className="page-container min-h-full">
      <div className="max-w-8xl mx-auto">

        {/* ── Page Header ── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6 pb-5"
             style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <div>
            <span className="eyebrow">Staff Roster</span>
            <h1 className="page-title text-3xl mt-1">
              {lang === 'es' ? 'Planificación de Personal' : 'Staff Planning'}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {lang === 'es'
                ? 'Gestión de rosters, guardias y previsión de demanda'
                : 'Roster management, guard activation and demand forecasting'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Config selector */}
            {configs.length > 0 && (
              <select
                value={selectedConfig?.id || ''}
                onChange={e => {
                  const c = configs.find(c => c.id === e.target.value);
                  if (c) setSelectedConfig(c);
                }}
                className="px-4 py-2.5 rounded-lg text-sm font-medium"
                style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)',
                         color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}
              >
                {configs.map(c => (
                  <option key={c.id} value={c.id}>{c.icao_code} — {c.name}</option>
                ))}
              </select>
            )}
            <button onClick={() => setShowCreateForm(true)} className="btn-primary">
              <Plus size={16} />
              {lang === 'es' ? 'Nueva Config.' : 'New Config'}
            </button>
          </div>
        </div>

        {/* ── Tab Navigation ── */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-1" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all duration-150 whitespace-nowrap"
                style={{
                  color: active ? 'var(--aipron-accent)' : 'var(--text-secondary)',
                  background: active ? 'var(--surface-raised)' : 'transparent',
                  borderBottom: active ? '2px solid var(--aipron-accent)' : '2px solid transparent',
                }}
              >
                <Icon size={16} />
                {tab.label[lang]}
                {tab.key === 'alerts' && alerts.filter(a => !a.resolved).length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                        style={{ background: 'var(--aipron-danger)', color: '#fff' }}>
                    {alerts.filter(a => !a.resolved).length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ══════════════════════════════════════ */}
        {/* TAB: CONFIG                            */}
        {/* ══════════════════════════════════════ */}
        {activeTab === 'config' && (
          <div className="space-y-6">
            {/* Create form modal */}
            {showCreateForm && (
              <div className="fixed inset-0 z-modal flex items-center justify-center p-4 animate-fade-in"
                   style={{ background: 'rgba(0,0,0,0.5)' }}>
                <div className="pro-card p-8 w-full max-w-xl animate-scale-in">
                  <h3 className="text-lg font-bold mb-6" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                    {lang === 'es' ? 'Nueva Configuración SDP' : 'New SDP Configuration'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-tertiary)' }}>ICAO</label>
                      <input type="text" maxLength={4} value={formData.icao_code}
                             onChange={e => setFormData(f => ({ ...f, icao_code: e.target.value }))}
                             placeholder="ELLX"
                             className="w-full px-3 py-2 rounded-lg text-sm"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-tertiary)' }}>
                        {lang === 'es' ? 'Nombre' : 'Name'}
                      </label>
                      <input type="text" value={formData.name}
                             onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                             placeholder="ELLX SDP Config 2026"
                             className="w-full px-3 py-2 rounded-lg text-sm"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-tertiary)' }}>
                        {lang === 'es' ? 'Días Trabajo' : 'Work Days'}
                      </label>
                      <input type="number" min={1} max={15} value={formData.work_days}
                             onChange={e => setFormData(f => ({ ...f, work_days: parseInt(e.target.value) || 6 }))}
                             className="w-full px-3 py-2 rounded-lg text-sm"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-tertiary)' }}>
                        {lang === 'es' ? 'Días Libres' : 'Off Days'}
                      </label>
                      <input type="number" min={1} max={15} value={formData.off_days}
                             onChange={e => setFormData(f => ({ ...f, off_days: parseInt(e.target.value) || 4 }))}
                             className="w-full px-3 py-2 rounded-lg text-sm"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-tertiary)' }}>DSR</label>
                      <input type="number" min={1} value={formData.dsr_override}
                             onChange={e => setFormData(f => ({ ...f, dsr_override: e.target.value }))}
                             placeholder="Auto (ADOSA)"
                             className="w-full px-3 py-2 rounded-lg text-sm"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-tertiary)' }}>Headcount</label>
                      <input type="number" min={1} value={formData.headcount_override}
                             onChange={e => setFormData(f => ({ ...f, headcount_override: e.target.value }))}
                             placeholder="Auto (ADOSA)"
                             className="w-full px-3 py-2 rounded-lg text-sm"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-tertiary)' }}>
                        {lang === 'es' ? 'On-Call (min)' : 'On-Call (min)'}
                      </label>
                      <input type="number" min={15} max={180} value={formData.on_call_response_minutes}
                             onChange={e => setFormData(f => ({ ...f, on_call_response_minutes: parseInt(e.target.value) || 60 }))}
                             className="w-full px-3 py-2 rounded-lg text-sm"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-tertiary)' }}>
                        {lang === 'es' ? 'Personas Guardia' : 'Guard Persons'}
                      </label>
                      <input type="number" min={0} max={10} value={formData.on_call_persons}
                             onChange={e => setFormData(f => ({ ...f, on_call_persons: parseInt(e.target.value) || 1 }))}
                             className="w-full px-3 py-2 rounded-lg text-sm"
                             style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <button onClick={() => setShowCreateForm(false)} className="btn-secondary">
                      {lang === 'es' ? 'Cancelar' : 'Cancel'}
                    </button>
                    <button onClick={handleCreateConfig} className="btn-primary"
                            disabled={!formData.icao_code || !formData.name}>
                      <Plus size={16} />
                      {lang === 'es' ? 'Crear' : 'Create'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Current config detail */}
            {selectedConfig ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Config info card */}
                <div className="pro-card p-6 lg:col-span-2">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                           style={{ background: 'linear-gradient(135deg, var(--aipron-accent), var(--aipron-accent-hover))', color: '#fff' }}>
                        <Plane size={22} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                          {selectedConfig.name}
                        </h3>
                        <span className="text-xs font-mono px-2 py-0.5 rounded"
                              style={{ background: 'var(--surface-raised)', color: 'var(--text-tertiary)', border: '1px solid var(--border-subtle)' }}>
                          {selectedConfig.icao_code}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteConfig(selectedConfig.id)} className="p-2 rounded-lg transition-colors"
                            style={{ color: 'var(--text-tertiary)' }}
                            onMouseEnter={e => { e.currentTarget.style.color = 'var(--aipron-danger)'; e.currentTarget.style.background = 'rgba(195,51,51,0.08)'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}>
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'DSR', value: selectedConfig.dsr_override || '—', icon: Users },
                      { label: 'Headcount', value: selectedConfig.headcount_override || '—', icon: Users },
                      { label: lang === 'es' ? 'Ciclo' : 'Cycle', value: `${selectedConfig.cycle_pattern?.work_days || 6}W / ${selectedConfig.cycle_pattern?.off_days || 4}D`, icon: RefreshCw },
                      { label: 'On-Call', value: `${selectedConfig.on_call_response_minutes} min`, icon: Clock },
                      { label: lang === 'es' ? 'Vacaciones' : 'Holidays', value: `${selectedConfig.holidays_per_person}d`, icon: Calendar },
                      { label: lang === 'es' ? 'Formación' : 'Training', value: `${selectedConfig.training_days_per_person}d`, icon: FileText },
                      { label: lang === 'es' ? 'Absentismo' : 'Absenteeism', value: `${selectedConfig.absenteeism_pct}%`, icon: TrendingUp },
                      { label: lang === 'es' ? 'Origen' : 'Source', value: selectedConfig.source.toUpperCase(), icon: Shield },
                    ].map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div key={i} className="p-3 rounded-lg" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}>
                          <div className="flex items-center gap-2 mb-1">
                            <Icon size={12} style={{ color: 'var(--text-tertiary)' }} />
                            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{item.label}</span>
                          </div>
                          <span className="text-lg font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>{item.value}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quick actions */}
                <div className="space-y-4">
                  {/* ADOSA Import */}
                  <div className="pro-card p-5">
                    <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                      {lang === 'es' ? 'Importar desde ADOSA' : 'Import from ADOSA'}
                    </h4>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                      {lang === 'es'
                        ? 'Vincula un proyecto DACAMS para importar DSR, headcount y ciclo automáticamente.'
                        : 'Link a DACAMS project to auto-import DSR, headcount and cycle.'}
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          const projs = await projectsService.getAll();
                          setProjects(projs.filter((p: Project) => p.analysis_status === 'COMPLETED'));
                        } catch (e) { console.error(e); }
                      }}
                      className="btn-secondary w-full text-sm"
                    >
                      <Download size={14} />
                      {lang === 'es' ? 'Seleccionar Proyecto' : 'Select Project'}
                    </button>
                    {projects.length > 0 && (
                      <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                        {projects.map(p => (
                          <button key={p.id} onClick={() => handleImportAdosa(p.id)}
                                  disabled={importing}
                                  className="w-full flex items-center justify-between p-2.5 rounded-lg text-left transition-colors text-sm"
                                  style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>
                            <div>
                              <span className="font-medium">{p.name}</span>
                              <span className="text-[10px] font-mono ml-2" style={{ color: 'var(--text-tertiary)' }}>{p.icao_code}</span>
                            </div>
                            <ArrowRight size={14} style={{ color: 'var(--aipron-accent)' }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Generated rosters list */}
                  <div className="pro-card p-5">
                    <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                      {lang === 'es' ? 'Rosters Generados' : 'Generated Rosters'}
                    </h4>
                    {rosters.length === 0 ? (
                      <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        {lang === 'es' ? 'Sin rosters aún.' : 'No rosters yet.'}
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {rosters.map(r => (
                          <button key={r.id} onClick={() => handleViewRoster(r.id)}
                                  className="w-full flex items-center justify-between p-2.5 rounded-lg text-left text-sm transition-colors"
                                  style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>
                            <div className="flex items-center gap-2">
                              {r.coverage_ok
                                ? <CheckCircle2 size={14} style={{ color: 'var(--aipron-success)' }} />
                                : <XCircle size={14} style={{ color: 'var(--aipron-danger)' }} />}
                              <span className="font-medium">
                                {r.roster_type === 'annual_generic' ? `Annual ${r.year}` : `${monthNames[(r.month || 1) - 1]} ${r.year}`}
                              </span>
                            </div>
                            <Eye size={14} style={{ color: 'var(--text-tertiary)' }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state py-20 rounded-xl" style={{ border: '2px dashed var(--border)', background: 'var(--surface-raised)' }}>
                <Settings className="empty-state-icon" />
                <h3 className="empty-state-title">
                  {lang === 'es' ? 'Sin configuraciones' : 'No configurations'}
                </h3>
                <p className="empty-state-desc mb-6">
                  {lang === 'es' ? 'Crea una configuración de roster para empezar.' : 'Create a roster config to start.'}
                </p>
                <button onClick={() => setShowCreateForm(true)} className="btn-primary">
                  <Plus size={16} /> {lang === 'es' ? 'Nueva Configuración' : 'New Configuration'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB: ANNUAL ROSTER                     */}
        {/* ══════════════════════════════════════ */}
        {activeTab === 'annual' && selectedConfig && (
          <div className="space-y-6">
            {/* Controls */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold uppercase" style={{ color: 'var(--text-tertiary)' }}>
                  {lang === 'es' ? 'Año' : 'Year'}
                </label>
                <input type="number" min={2020} max={2050} value={rosterYear}
                       onChange={e => setRosterYear(parseInt(e.target.value))}
                       className="px-3 py-2 rounded-lg text-sm w-24"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
              </div>
              <button onClick={() => handleGenerateRoster('annual')} className="btn-primary"
                      disabled={generating || !selectedConfig.dsr_override}>
                {generating ? <RefreshCw size={16} className="animate-spin" /> : <Zap size={16} />}
                {lang === 'es' ? 'Generar Roster Anual' : 'Generate Annual Roster'}
              </button>
              {!selectedConfig.dsr_override && (
                <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(195,51,51,0.08)', color: 'var(--aipron-danger)' }}>
                  {lang === 'es' ? 'Configura DSR primero' : 'Set DSR first'}
                </span>
              )}
            </div>

            {/* Annual roster visualization */}
            {activeRoster && activeRoster.roster_type === 'annual_generic' ? (
              <div className="space-y-6">
                {/* Coverage chart */}
                <div className="pro-card p-5">
                  <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                    {lang === 'es' ? 'Cobertura Diaria vs DSR' : 'Daily Coverage vs DSR'}
                  </h4>
                  <div className="flex items-end gap-px h-32 overflow-x-auto">
                    {(activeRoster.daily_coverage || []).map((cov, i) => {
                      const dsr = activeRoster.dsr || 10;
                      const maxCov = Math.max(...(activeRoster.daily_coverage || [1]));
                      const h = Math.max(4, (cov / maxCov) * 100);
                      const ok = cov >= dsr;
                      return (
                        <div key={i} className="flex-shrink-0 rounded-t-sm transition-colors" title={`Day ${i + 1}: ${cov}`}
                             style={{
                               width: '2px', height: `${h}%`,
                               background: ok ? 'var(--aipron-success)' : 'var(--aipron-danger)',
                               opacity: ok ? 0.7 : 1,
                             }} />
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ background: 'var(--aipron-success)' }} /> ≥ DSR
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ background: 'var(--aipron-danger)' }} /> {'<'} DSR
                    </span>
                    <span>DSR = {activeRoster.dsr} | HC = {activeRoster.headcount}</span>
                  </div>
                </div>

                {/* Monthly summary cards */}
                {activeRoster.months_summary && (
                  <div className="pro-card p-5">
                    <h4 className="text-sm font-bold mb-4" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                      {lang === 'es' ? 'Resumen Mensual' : 'Monthly Summary'}
                    </h4>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
                      {activeRoster.months_summary.map((m: any, i: number) => (
                        <div key={i} className="p-3 rounded-lg text-center" style={{
                          background: m.days_below_dsr > 0 ? 'rgba(195,51,51,0.06)' : 'var(--surface-raised)',
                          border: `1px solid ${m.days_below_dsr > 0 ? 'var(--aipron-danger)' : 'var(--border-subtle)'}`,
                        }}>
                          <div className="text-[10px] font-bold uppercase mb-1" style={{ color: 'var(--text-tertiary)' }}>{m.month}</div>
                          <div className="text-lg font-bold" style={{ color: m.days_below_dsr > 0 ? 'var(--aipron-danger)' : 'var(--text-primary)' }}>
                            {m.avg_coverage}
                          </div>
                          <div className="text-[9px]" style={{ color: 'var(--text-tertiary)' }}>
                            {m.min_coverage}–{m.max_coverage}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Employee stats */}
                {activeRoster.employee_stats && (
                  <div className="pro-card p-5">
                    <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                      {lang === 'es' ? 'Estadísticas por Empleado' : 'Employee Statistics'}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                            <th className="text-left py-2 px-2 font-semibold uppercase" style={{ color: 'var(--text-tertiary)' }}>#</th>
                            <th className="text-center py-2 px-2 font-semibold uppercase" style={{ color: 'var(--text-tertiary)' }}>T</th>
                            <th className="text-center py-2 px-2 font-semibold uppercase" style={{ color: 'var(--text-tertiary)' }}>L</th>
                            <th className="text-center py-2 px-2 font-semibold uppercase" style={{ color: 'var(--text-tertiary)' }}>V</th>
                            <th className="text-center py-2 px-2 font-semibold uppercase" style={{ color: 'var(--text-tertiary)' }}>F</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeRoster.employee_stats.slice(0, 20).map((emp: any) => (
                            <tr key={emp.employee} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                              <td className="py-1.5 px-2 font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{emp.employee}</td>
                              <td className="text-center py-1.5 px-2" style={{ color: CELL_COLORS.T }}>{emp.work_days}</td>
                              <td className="text-center py-1.5 px-2" style={{ color: CELL_COLORS.L }}>{emp.off_days}</td>
                              <td className="text-center py-1.5 px-2" style={{ color: CELL_COLORS.V }}>{emp.vacation_days}</td>
                              <td className="text-center py-1.5 px-2" style={{ color: CELL_COLORS.F }}>{emp.training_days}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state py-16 rounded-xl" style={{ border: '2px dashed var(--border)', background: 'var(--surface-raised)' }}>
                <Calendar className="empty-state-icon" />
                <h3 className="empty-state-title">
                  {lang === 'es' ? 'Sin roster anual generado' : 'No annual roster generated'}
                </h3>
                <p className="empty-state-desc">
                  {lang === 'es' ? 'Genera un roster anual para visualizar la planificación.' : 'Generate an annual roster to view planning.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB: MONTHLY ROSTER                    */}
        {/* ══════════════════════════════════════ */}
        {activeTab === 'monthly' && selectedConfig && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold uppercase" style={{ color: 'var(--text-tertiary)' }}>{lang === 'es' ? 'Año' : 'Year'}</label>
                <input type="number" min={2020} max={2050} value={rosterYear}
                       onChange={e => setRosterYear(parseInt(e.target.value))}
                       className="px-3 py-2 rounded-lg text-sm w-24"
                       style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }} />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold uppercase" style={{ color: 'var(--text-tertiary)' }}>{lang === 'es' ? 'Mes' : 'Month'}</label>
                <select value={rosterMonth} onChange={e => setRosterMonth(parseInt(e.target.value))}
                        className="px-3 py-2 rounded-lg text-sm"
                        style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>
                  {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <button onClick={() => handleGenerateRoster('monthly')} className="btn-primary"
                      disabled={generating || !selectedConfig.dsr_override}>
                {generating ? <RefreshCw size={16} className="animate-spin" /> : <Zap size={16} />}
                {lang === 'es' ? 'Generar Roster Mensual' : 'Generate Monthly Roster'}
              </button>
            </div>

            {activeRoster && activeRoster.roster_type === 'monthly_specific' && activeRoster.roster_data ? (
              <div className="pro-card p-5 overflow-x-auto">
                <h4 className="text-sm font-bold mb-4" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                  {lang === 'es' ? 'Cuadrante Mensual' : 'Monthly Grid'} — {monthNames[(activeRoster.month || 1) - 1]} {activeRoster.year}
                </h4>
                <table className="text-[10px]" style={{ borderCollapse: 'separate', borderSpacing: '1px' }}>
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left font-bold" style={{ color: 'var(--text-tertiary)', position: 'sticky', left: 0, background: 'var(--bg)', zIndex: 1 }}>
                        {lang === 'es' ? 'EMP' : 'EMP'}
                      </th>
                      {Array.from({ length: activeRoster.roster_data[0]?.length || 0 }, (_, d) => (
                        <th key={d} className="px-1.5 py-1 text-center font-bold" style={{ color: 'var(--text-tertiary)' }}>
                          {d + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeRoster.roster_data.map((row, emp) => (
                      <tr key={emp}>
                        <td className="px-2 py-0.5 font-bold font-mono" style={{ color: 'var(--text-primary)', position: 'sticky', left: 0, background: 'var(--bg)', zIndex: 1 }}>
                          {emp + 1}
                        </td>
                        {row.map((cell, d) => (
                          <td key={d} className="px-1.5 py-0.5 text-center font-bold rounded-sm"
                              style={{
                                background: cell === 'T' ? 'rgba(29,125,80,0.12)' : cell === 'V' ? 'rgba(124,58,237,0.12)' : cell === 'F' ? 'rgba(37,99,235,0.12)' : 'transparent',
                                color: CELL_COLORS[cell] || 'var(--text-tertiary)',
                              }}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* Coverage row */}
                    <tr style={{ borderTop: '2px solid var(--border)' }}>
                      <td className="px-2 py-1 font-bold text-[10px] uppercase" style={{ color: 'var(--aipron-accent)', position: 'sticky', left: 0, background: 'var(--bg)', zIndex: 1 }}>
                        COV
                      </td>
                      {(activeRoster.daily_coverage || []).map((cov, d) => (
                        <td key={d} className="px-1.5 py-1 text-center font-bold"
                            style={{ color: cov >= (activeRoster.dsr || 0) ? 'var(--aipron-success)' : 'var(--aipron-danger)' }}>
                          {cov}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="flex items-center gap-1"><span className="font-bold" style={{ color: CELL_COLORS.T }}>T</span> {lang === 'es' ? 'Trabajo' : 'Work'}</span>
                  <span className="flex items-center gap-1"><span className="font-bold" style={{ color: CELL_COLORS.L }}>L</span> {lang === 'es' ? 'Libre' : 'Off'}</span>
                  <span className="flex items-center gap-1"><span className="font-bold" style={{ color: CELL_COLORS.V }}>V</span> {lang === 'es' ? 'Vacaciones' : 'Holiday'}</span>
                  <span className="flex items-center gap-1"><span className="font-bold" style={{ color: CELL_COLORS.F }}>F</span> {lang === 'es' ? 'Formación' : 'Training'}</span>
                </div>
              </div>
            ) : (
              <div className="empty-state py-16 rounded-xl" style={{ border: '2px dashed var(--border)', background: 'var(--surface-raised)' }}>
                <CalendarDays className="empty-state-icon" />
                <h3 className="empty-state-title">{lang === 'es' ? 'Sin cuadrante generado' : 'No monthly grid generated'}</h3>
                <p className="empty-state-desc">{lang === 'es' ? 'Genera un roster mensual para ver el cuadrante.' : 'Generate a monthly roster to see the grid.'}</p>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB: TRAFFIC FORECAST                  */}
        {/* ══════════════════════════════════════ */}
        {activeTab === 'traffic' && selectedConfig && (
          <div className="space-y-6">
            <div className="pro-card p-6">
              <h4 className="text-sm font-bold mb-3" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                {lang === 'es' ? 'Subir Previsión de Tráfico' : 'Upload Traffic Forecast'}
              </h4>
              <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
                {lang === 'es'
                  ? 'Sube un CSV con la previsión de movimientos. Se ejecutará un análisis DACAMS completo para identificar días de alta demanda.'
                  : 'Upload a CSV with movement forecast. A full DACAMS analysis will run to identify high-demand days.'}
              </p>
              <div className="flex items-center gap-4">
                <label className="btn-primary cursor-pointer">
                  <Upload size={16} />
                  {uploadingForecast ? (lang === 'es' ? 'Analizando...' : 'Analyzing...') : (lang === 'es' ? 'Subir CSV' : 'Upload CSV')}
                  <input type="file" accept=".csv" className="hidden" onChange={handleUploadForecast} disabled={uploadingForecast} />
                </label>
                <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                  {lang === 'es' ? 'Formato: date, total_movements, arrivals, departures, peak_hour_movements' : 'Format: date, total_movements, arrivals, departures, peak_hour_movements'}
                </span>
              </div>
            </div>

            {/* Forecast results placeholder */}
            <div className="empty-state py-12 rounded-xl" style={{ border: '2px dashed var(--border)', background: 'var(--surface-raised)' }}>
              <BarChart3 className="empty-state-icon" />
              <h3 className="empty-state-title">{lang === 'es' ? 'Sin previsiones subidas' : 'No forecasts uploaded'}</h3>
              <p className="empty-state-desc">{lang === 'es' ? 'Sube un CSV para ver el análisis de demanda.' : 'Upload a CSV to see demand analysis.'}</p>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB: ALERTS & GUARDS                   */}
        {/* ══════════════════════════════════════ */}
        {activeTab === 'alerts' && selectedConfig && (
          <div className="space-y-6">
            {/* D+1 Evaluation */}
            <div className="pro-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-sm font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                    {lang === 'es' ? 'Evaluación D+1 — ¿Necesita guardia mañana?' : 'D+1 Evaluation — Guard needed tomorrow?'}
                  </h4>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                    {lang === 'es'
                      ? `Consulta la previsión meteorológica y de tráfico para evaluar la necesidad de activar guardia. On-call: ${selectedConfig.on_call_response_minutes} min.`
                      : `Checks the weather and traffic forecast to evaluate guard activation need. On-call: ${selectedConfig.on_call_response_minutes} min.`}
                  </p>
                </div>
                <button onClick={handleEvaluateGuard} className="btn-primary" disabled={evaluating}>
                  {evaluating ? <RefreshCw size={16} className="animate-spin" /> : <Shield size={16} />}
                  {lang === 'es' ? 'Evaluar' : 'Evaluate'}
                </button>
              </div>

              {guardEvaluation && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <div className="p-4 rounded-xl text-center" style={{
                    background: guardEvaluation.guard_needed ? 'rgba(195,51,51,0.06)' : 'rgba(29,125,80,0.06)',
                    border: `1px solid ${guardEvaluation.guard_needed ? 'var(--aipron-danger)' : 'var(--aipron-success)'}`,
                  }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: guardEvaluation.guard_needed ? 'var(--aipron-danger)' : 'var(--aipron-success)' }}>
                      {guardEvaluation.guard_needed ? '⚠️' : '✅'}
                    </div>
                    <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                      {guardEvaluation.guard_needed
                        ? (lang === 'es' ? 'GUARDIA NECESARIA' : 'GUARD NEEDED')
                        : (lang === 'es' ? 'SIN NECESIDAD' : 'NOT NEEDED')}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}>
                    <div className="text-[10px] font-bold uppercase mb-2" style={{ color: 'var(--text-tertiary)' }}>
                      {lang === 'es' ? 'Meteorología' : 'Weather'}
                    </div>
                    <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                      {guardEvaluation.weather_assessment?.category || 'VMC'}
                    </div>
                    <div className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                      {guardEvaluation.weather_assessment?.status === 'no_data'
                        ? (lang === 'es' ? 'Sin datos meteo' : 'No weather data')
                        : `Wind: ${guardEvaluation.weather_assessment?.wind_speed || 0} kt | Vis: ${guardEvaluation.weather_assessment?.visibility || 9999}m`}
                    </div>
                  </div>
                  <div className="p-4 rounded-xl" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}>
                    <div className="text-[10px] font-bold uppercase mb-2" style={{ color: 'var(--text-tertiary)' }}>
                      {lang === 'es' ? 'Alertas Generadas' : 'Alerts Generated'}
                    </div>
                    <div className="text-2xl font-bold" style={{ color: guardEvaluation.alerts_generated > 0 ? 'var(--aipron-danger)' : 'var(--text-primary)' }}>
                      {guardEvaluation.alerts_generated}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Active alerts list */}
            <div className="pro-card p-6">
              <h4 className="text-sm font-bold mb-4" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
                {lang === 'es' ? 'Alertas Activas' : 'Active Alerts'}
              </h4>
              {alerts.length === 0 ? (
                <p className="text-xs py-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
                  {lang === 'es' ? 'Sin alertas activas. Ejecuta una evaluación D+1.' : 'No active alerts. Run a D+1 evaluation.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {alerts.map(alert => (
                    <div key={alert.id}
                         className="flex items-center justify-between p-4 rounded-lg transition-colors"
                         style={{
                           background: alert.resolved ? 'var(--surface-raised)' : (alert.severity === 'critical' ? 'rgba(195,51,51,0.06)' : 'rgba(176,125,22,0.06)'),
                           border: `1px solid ${alert.resolved ? 'var(--border-subtle)' : (alert.severity === 'critical' ? 'var(--aipron-danger)' : 'var(--aipron-warning)')}`,
                           opacity: alert.resolved ? 0.6 : 1,
                         }}>
                      <div className="flex items-center gap-3">
                        {alert.severity === 'critical'
                          ? <AlertTriangle size={18} style={{ color: 'var(--aipron-danger)' }} />
                          : <Shield size={18} style={{ color: 'var(--aipron-warning)' }} />}
                        <div>
                          <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{alert.reason}</div>
                          <div className="flex items-center gap-3 mt-1 text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                            <span>{alert.target_date}</span>
                            <span className="uppercase font-bold">{alert.source}</span>
                            <span className={`px-1.5 py-0.5 rounded font-bold ${alert.severity === 'critical' ? 'badge-danger' : 'badge-warning'}`}>
                              {alert.severity.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                      {!alert.resolved && (
                        <button onClick={() => handleResolveAlert(alert.id)}
                                className="btn-secondary text-xs px-3 py-1.5">
                          <CheckCircle2 size={12} />
                          {lang === 'es' ? 'Resolver' : 'Resolve'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StaffRosterPage;
